import { useParams, useNavigate, Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  Edit3,
  Users,
  UserPlus,
  GraduationCap,
  ArrowLeft,
  BarChart3,
  Brain,
  BookOpen,
  Play,
  Clock,
  Share,
  Download,
  SparkleIcon,
} from "lucide-react";
import {
  exportInteractiveMaterialAsH5P,
  useGetInteractiveMaterial,
  useGetTeacherAssignedInteractiveMaterials,
} from "@packages/api/madlenAPI";
import AssignTab from "@features/tools/components/interactive-materials/AssignTab";
import { useState, useEffect, useMemo } from "react";
import { Text, Button } from "@core/index";
import {
  InteractiveMaterialResponseDtoStatus,
  StudentInteractiveMaterialResponseDto,
} from "@packages/api/madlenAPI.schemas";
import { InteractiveMaterialResponseDto } from "@packages/api/madlenAPI.schemas";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { StudentInteractiveMaterialResponseDtoStatus } from "@packages/api/madlenAPI.schemas";
import LoadingContainer from "@packages/components/Loading";
import { duration } from "html2canvas/dist/types/css/property-descriptors/duration";
import { useToast } from "@packages/contexts/ToastContext";
import { getEnvironment } from "@packages/utils";

const calculateScore = (
  student: StudentInteractiveMaterialResponseDto,
  material: InteractiveMaterialResponseDto,
) => {
  const totalQuestions =
    (material?.generatedQuizQuestions?.length || 0) +
    (material?.generatedVideoQuestions?.length || 0);
  const correctAnswers =
    student.quizAnswers.filter((answer) => answer.isCorrect).length +
    student.videoQuizAnswers.filter((answer) => answer.isCorrect).length;
  return Math.round((correctAnswers / totalQuestions) * 100);
};

const calculateProgress = (
  student: StudentInteractiveMaterialResponseDto,
  material: InteractiveMaterialResponseDto,
) => {
  const totalQuestions =
    (material?.generatedQuizQuestions?.length || 0) +
    (material?.generatedVideoQuestions?.length || 0);

  if (totalQuestions === 0) return 0;

  const answeredQuestions =
    student.quizAnswers.length + student.videoQuizAnswers.length;
  return Math.round((answeredQuestions / totalQuestions) * 100);
};

const StatCard = ({
  icon,
  title,
  value,
  trend,
  className,
}: {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  className?: string;
}) => {
  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-100 dark:border-gray-700 ${className}`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-rose-100 dark:bg-rose-900/30 rounded-lg flex items-center justify-center">
          {icon}
        </div>
        <Text variant={"h3"}>{title}</Text>
      </div>
      <Text variant={"h2"} size={"3xl"}>
        {value}
      </Text>
    </div>
  );
};

const SingleInteractiveMaterialView = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showAssignTab, setShowAssignTab] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState<
    {
      classroomId: number;
      studentIds: number[];
    }[]
  >([]);
  const [selectedMetric, setSelectedMetric] = useState<"progress" | "score">(
    "progress",
  );
  const { addToast } = useToast();
  const [exportLoading, setExportLoading] = useState(false);

  const {
    data: material,
    isLoading,
    error,
  } = useGetInteractiveMaterial(Number(id));

  const {
    data: assignedMaterials,
    refetch: refetchAssignedStudents,
    queryKey: assignedStudentsQueryKey,
    isLoading: isLoadingAssigned,
  } = useGetTeacherAssignedInteractiveMaterials();
  const currentMaterialStudents =
    assignedMaterials?.filter(
      (sm) => sm.interactiveMaterialId === Number(id),
    ) || [];

  const assignedStudentIds = useMemo(
    () => currentMaterialStudents.map((student) => student.studentId),
    [currentMaterialStudents],
  );

  // Set showAssignTab to true by default if there are no students
  useEffect(() => {
    if (isLoadingAssigned && currentMaterialStudents.length === 0) {
      setShowAssignTab(true);
    }
  }, [currentMaterialStudents.length, isLoadingAssigned]);

  const stats = useMemo(() => {
    if (!currentMaterialStudents.length) return null;

    const totalStudents = currentMaterialStudents.length;
    const completedStudents = currentMaterialStudents.filter(
      (s) => s.status === StudentInteractiveMaterialResponseDtoStatus.COMPLETED,
    ).length;
    const scores = currentMaterialStudents.map((s) =>
      calculateScore(s, material!),
    );
    const averageScore =
      scores.reduce((acc, score) => acc + score, 0) / totalStudents;

    return {
      totalStudents,
      averageScore: Math.round(averageScore),
      completionRate: Math.round((completedStudents / totalStudents) * 100),
    };
  }, [currentMaterialStudents]);

  const chartData = useMemo(() => {
    if (!currentMaterialStudents.length) return [];

    return currentMaterialStudents.map((student) => {
      const progress = calculateProgress(student, material!);
      return {
        student: `${student.studentName}`,
        progress,
        score: calculateScore(student, material!),
        tooltipProgress: `${t("common.progress")}: ${progress}`,
      };
    });
  }, [currentMaterialStudents, t]);

  const isTed =
    window.location.hostname === "ted.madlen.io" ||
    window.location.hostname === "tedankara.madlen.io";

  if (isLoading || isLoadingAssigned) {
    return (
      <LoadingContainer
        title={t("interactive_material.loading.title")}
        themeColor="rose"
      />
    );
  }

  if (error || !material) {
    return (
      <div className="text-center text-gray-600 dark:text-gray-300">
        {t("interactive_material.error.loadingError")}
      </div>
    );
  }

  return (
    <div className="flex-1 md:p-8 p-4 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex flex-col gap-6">
            <div className="flex items-start gap-4">
              <Button
                variant="ghost"
                size="icon"
                rounded="full"
                onClick={() =>
                  navigate(
                    material.status ===
                      InteractiveMaterialResponseDtoStatus.EDITING
                      ? `${location.pathname}/edit`
                      : "/content/interactive-materials",
                  )
                }
                className="w-10 h-10 flex-shrink-0"
                bgColor="bg-rose-100 hover:bg-rose-200 dark:bg-rose-900/30 dark:hover:bg-rose-900/50"
                textColor="text-rose-600 dark:text-rose-400"
              >
                <ArrowLeft size={22} />
              </Button>
              <div>
                <Text variant={"h1"} className="mb-2 text-lg md:text-3xl">
                  {material.title ||
                    material.youtubeTitle ||
                    t("quickActions.interactiveMaterial")}
                </Text>
                <Text variant={"p"} status="subtle" size={"base"}>
                  {t("interactive_material.home.subtitle")}
                </Text>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              {currentMaterialStudents.length === 0 &&
                (getEnvironment() === "DEV" || isTed) && (
                  <Button
                    onClick={() =>
                      navigate(
                        `/content/interactive-materials/${id}/advanced-edit`,
                      )
                    }
                    variant="ghost"
                    size="lg"
                    rounded="xl"
                    className="flex items-center gap-2 bg-rose-50 hover:bg-rose-100 dark:bg-rose-900/20 dark:hover:bg-rose-900/30 border-none shadow-sm"
                    textColor="text-rose-600 dark:text-rose-400"
                  >
                    <SparkleIcon className="w-5 h-5" />
                    <span className="font-medium hidden sm:inline">
                      {t("interactive_material.home.advancedEditTitle")}
                    </span>
                  </Button>
                )}
              {currentMaterialStudents.length === 0 && (
                <Button
                  onClick={() =>
                    navigate(`/content/interactive-materials/${id}/edit`)
                  }
                  variant="ghost"
                  size="lg"
                  rounded="xl"
                  className="flex items-center gap-2 bg-rose-50 hover:bg-rose-100 dark:bg-rose-900/20 dark:hover:bg-rose-900/30 border-none shadow-sm"
                  textColor="text-rose-600 dark:text-rose-400"
                >
                  <Edit3 className="w-5 h-5" />
                  <span className="font-medium hidden sm:inline">
                    {t("interactive_material.home.editTitle")}
                  </span>
                </Button>
              )}
              <Button
                onClick={() => window.open(`/h5p/${id}`, "_blank")}
                variant="ghost"
                size="lg"
                rounded="xl"
                className="flex items-center gap-2 bg-rose-50 hover:bg-rose-100 dark:bg-rose-900/20 dark:hover:bg-rose-900/30 border-none shadow-sm"
                textColor="text-rose-600 dark:text-rose-400"
              >
                <Play className="w-5 h-5" />
                <span className="font-medium hidden sm:inline">
                  {t("interactive_material.home.playTitle")}
                </span>
              </Button>
              {(getEnvironment() === "DEV" || isTed) && (
                <Button
                  onClick={async () => {
                    setExportLoading(true);
                    const response = await exportInteractiveMaterialAsH5P(
                      Number(id),
                    );
                    const url = response.downloadUrl;
                    if (!url) {
                      addToast({
                        title: t("interactive_material.home.downloadError"),
                        description: t(
                          "interactive_material.home.downloadErrorDescription",
                        ),
                        variant: "error",
                      });
                      return;
                    }
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${material.title}.h5p`;
                    a.click();
                    URL.revokeObjectURL(url);
                    setExportLoading(false);
                  }}
                  variant="ghost"
                  size="lg"
                  rounded="xl"
                  className="transition-all duration-300 flex items-center gap-2 bg-rose-50 hover:bg-rose-100 dark:bg-rose-900/20 dark:hover:bg-rose-900/30 border-none shadow-sm"
                  textColor="text-rose-600 dark:text-rose-400"
                  loading={exportLoading}
                >
                  <Download className="w-5 h-5" />
                  <span className="font-medium hidden sm:inline">
                    {t("interactive_material.home.downloadTitle")}
                  </span>
                </Button>
              )}
            </div>
          </div>
        </div>

        {currentMaterialStudents.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <StatCard
                icon={
                  <Users className="w-6 h-6 text-rose-600 dark:text-rose-400" />
                }
                title={t("interactive_material.review.stats.totalStudents")}
                value={stats?.totalStudents || 0}
              />
              <StatCard
                icon={
                  <Brain className="w-6 h-6 text-rose-600 dark:text-rose-400" />
                }
                title={t("interactive_material.review.stats.averageScore")}
                value={`${stats?.averageScore || 0}%`}
              />
              <StatCard
                icon={
                  <BookOpen className="w-6 h-6 text-rose-600 dark:text-rose-400" />
                }
                title={t("interactive_material.review.stats.completionRate")}
                value={`${stats?.completionRate || 0}%`}
              />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <Text variant={"h2"} className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-rose-500" />
                  {t("interactive_material.review.performanceChart")}
                </Text>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setSelectedMetric("progress")}
                    size="md"
                    rounded="lg"
                    bgColor={
                      selectedMetric === "progress"
                        ? "bg-rose-100 dark:bg-rose-900/30"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700"
                    }
                    textColor={
                      selectedMetric === "progress"
                        ? "text-rose-600 dark:text-rose-400"
                        : "text-gray-600 dark:text-gray-300"
                    }
                  >
                    {t("interactive_material.review.metrics.progress")}
                  </Button>
                  <Button
                    onClick={() => setSelectedMetric("score")}
                    size="md"
                    rounded="lg"
                    bgColor={
                      selectedMetric === "score"
                        ? "bg-rose-100 dark:bg-rose-900/30"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700"
                    }
                    textColor={
                      selectedMetric === "score"
                        ? "text-rose-600 dark:text-rose-400"
                        : "text-gray-600 dark:text-gray-300"
                    }
                  >
                    {t("interactive_material.review.metrics.score")}
                  </Button>
                </div>
              </div>
              <div className="h-96 w-full overflow-x-auto">
                <div className="h-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 70,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="student"
                        height={60}
                        angle={-45}
                        textAnchor="end"
                        interval={0}
                        tick={{ fontSize: 12 }}
                        // Add this to make labels wrap when needed
                        tickFormatter={(value) => {
                          return value.length > 10
                            ? `${value.substring(0, 10)}...`
                            : value;
                        }}
                      />
                      <YAxis domain={[0, 100]} />
                      <Tooltip
                        formatter={(value, name) => {
                          if (name === "progress") {
                            return [value, t("common.progress")];
                          }
                          if (name === "score") {
                            return [
                              value,
                              t("interactive_material.review.metrics.score"),
                            ];
                          }
                          return [value, name];
                        }}
                        labelFormatter={(label) => label}
                      />
                      <Bar
                        dataKey={selectedMetric}
                        fill="#f43f5e"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={50}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden mb-8">
          {showAssignTab ? (
            <div className="px-6 py-4 flex flex-col">
              <Button
                onClick={() => {
                  const link = `${window.location.origin}/material/${id}`;
                  navigator.clipboard.writeText(link);
                  addToast({
                    title: t("interactive_material.home.shareTitle"),
                    description: t(
                      "interactive_material.home.shareDescription",
                    ),
                    variant: "success",
                    duration: 3500,
                  });
                }}
                size="md"
                rounded="lg"
                bgColor="bg-rose-100 dark:bg-rose-900/30 hover:bg-rose-200 dark:hover:bg-rose-900/50"
                textColor="text-rose-600 dark:text-rose-400"
                className="flex items-center gap-2 self-end"
              >
                <Share className="w-5 h-5" />
                <span className="font-medium hidden sm:inline">
                  {t("common.shareWithTeachers")}
                </span>
              </Button>
              <AssignTab
                selectedStudents={selectedStudents}
                setSelectedStudents={setSelectedStudents}
                setShowAssignTab={setShowAssignTab}
                id={id!}
                assignedStudentsQueryKey={assignedStudentsQueryKey}
                refetchAssignedStudents={refetchAssignedStudents}
                assignedStudentIds={assignedStudentIds}
              />
            </div>
          ) : (
            <div className="space-y-4 my-4 mx-6">
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-2">
                  <Text variant={"h2"} className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-rose-500" />
                    {t("interactive_material.review.studentProgress")}
                  </Text>
                  <Text variant={"p"} status="secondary" className="mb-4">
                    interactive_material.review.clickStudentCard
                  </Text>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setShowAssignTab(true)}
                    size="md"
                    rounded="lg"
                    bgColor="bg-rose-100 dark:bg-rose-900/30 hover:bg-rose-200 dark:hover:bg-rose-900/50"
                    textColor="text-rose-600 dark:text-rose-400"
                    className="flex items-center gap-2"
                  >
                    <UserPlus className="w-5 h-5" />
                    {t("interactive_material.assignStudents")}
                  </Button>
                  <Button
                    onClick={() => {
                      const link = `${window.location.origin}/material/${id}`;
                      navigator.clipboard.writeText(link);
                      addToast({
                        title: t("interactive_material.home.shareTitle"),
                        description: t(
                          "interactive_material.home.shareDescription",
                        ),
                        variant: "success",
                        duration: 3500,
                      });
                    }}
                    size="md"
                    rounded="lg"
                    bgColor="bg-rose-100 dark:bg-rose-900/30 hover:bg-rose-200 dark:hover:bg-rose-900/50"
                    textColor="text-rose-600 dark:text-rose-400"
                    className="flex items-center gap-2"
                  >
                    <Share className="w-5 h-5" />
                    <span className="font-medium hidden sm:inline">
                      {t("common.shareWithTeachers")}
                    </span>
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentMaterialStudents.map((student) => (
                  <div
                    key={student.studentId}
                    onClick={() => {
                      if (
                        student.status !==
                        StudentInteractiveMaterialResponseDtoStatus.COMPLETED
                      )
                        return;

                      navigate(
                        `/content/interactive-materials/${id}/review/student/${student.studentId}`,
                      );
                    }}
                    className={`group bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md border border-gray-100 dark:border-gray-700 ${student.status === StudentInteractiveMaterialResponseDtoStatus.COMPLETED ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50" : "pointer-events-none opacity-80"} transition-colors duration-200 overflow-hidden`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 md:space-x-3 overflow-hidden">
                        <div className="w-8 h-8 md:w-10 md:h-10 flex-shrink-0 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center group-hover:bg-rose-200 dark:group-hover:bg-rose-900/50 transition-colors duration-200">
                          <GraduationCap className="w-5 h-5 md:w-6 md:h-6 text-rose-600 dark:text-rose-400" />
                        </div>
                        <div className="min-w-0">
                          <Text
                            variant={"p"}
                            weight={"medium"}
                            className="group-hover:text-rose-600 dark:group-hover:text-rose-400 transition-colors duration-200 truncate"
                          >
                            {student.studentName}
                          </Text>
                        </div>
                      </div>
                      {student.status ===
                      StudentInteractiveMaterialResponseDtoStatus.COMPLETED ? (
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0">
                          <ArrowLeft className="w-5 h-5 text-rose-600 dark:text-rose-400 rotate-180" />
                        </div>
                      ) : (
                        <div className="flex items-center flex-shrink-0">
                          <Clock className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="mt-4">
                      <div className="flex justify-between mb-1">
                        <Text variant="span" size="sm" status="secondary">
                          {t("common.progress")}
                        </Text>
                        <Text variant="span" size="sm" status="subtle">
                          {calculateProgress(student, material!)}%
                        </Text>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-rose-500 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${calculateProgress(student, material!)}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleInteractiveMaterialView;
